// Apolo IDE — LearningPanel root (WS L6.1).
//
// Side-panel tab that exposes the Learning Loop pipeline to the user.
// Four sub-tabs: Candidates (pending decisions), Skills (auto-generated),
// Memories (applied), Metrics (counts dashboard).
//
// On mount, the panel also starts a background poller that pulls any
// status='approved' candidates from the Worker and commits them to the
// vault via Brain MCP (vaultCommit.pumpApprovedCandidates). This is the
// renderer-side half of D-002 (the Worker cannot reach Brain MCP).

import { useEffect, useRef, useState } from 'react';
import { Brain, Library, Sparkles, BarChart3, Activity, Palette } from 'lucide-react';
import Candidates from './Candidates.jsx';
import Skills from './Skills.jsx';
import Memories from './Memories.jsx';
import Metrics from './Metrics.jsx';
import Status from './Status.jsx';
import CraftReview from './CraftReview.jsx';
import { pumpApprovedCandidates } from '../../../../lib/learning/vaultCommit.js';
import { pumpApprovedCraftRules } from '../../../../lib/learning/craftRulesPump.js';

const SUB_TABS = [
  { id: 'status', label: 'Status', Icon: Activity, Component: Status },
  { id: 'candidates', label: 'Candidates', Icon: Sparkles, Component: Candidates },
  { id: 'craft', label: 'Craft', Icon: Palette, Component: CraftReview },
  { id: 'skills', label: 'Skills', Icon: Brain, Component: Skills },
  { id: 'memories', label: 'Memories', Icon: Library, Component: Memories },
  { id: 'metrics', label: 'Metrics', Icon: BarChart3, Component: Metrics },
];

const PUMP_INTERVAL_MS = 10_000;

export default function LearningPanel({ sessionId, sessionDirectory } = {}) {
  const [sub, setSub] = useState('candidates');
  const [lastPump, setLastPump] = useState(null);
  const pumpRunning = useRef(false);

  useEffect(() => {
    async function pump() {
      if (pumpRunning.current) return;
      pumpRunning.current = true;
      try {
        const res = await pumpApprovedCandidates();
        if (res.processed > 0 || res.error) setLastPump(res);
        // Craft loop: apply human-approved craft rules + materialize craft tests
        // for the active session's project. No-op (skipped) without a worktree.
        await pumpApprovedCraftRules({ root: sessionDirectory, sessionId });
      } catch {
        /* swallow — keep UI responsive */
      } finally {
        pumpRunning.current = false;
      }
    }
    // Kick once on mount, then poll.
    pump();
    const timer = setInterval(pump, PUMP_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [sessionId, sessionDirectory]);

  const ActiveBody = SUB_TABS.find((t) => t.id === sub)?.Component || Candidates;

  return (
    <section aria-label="Learning" className="flex h-full flex-col">
      <header className="flex items-center justify-between border-b border-apolo-borderSubtle px-3 py-2">
        <nav role="tablist" aria-label="Learning sub-tabs" className="flex items-center gap-1">
          {SUB_TABS.map((t) => {
            const isActive = t.id === sub;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setSub(t.id)}
                className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs transition focus:outline-none focus:ring-2 focus:ring-apolo-accent/50 ${
                  isActive
                    ? 'bg-apolo-surface2 text-apolo-textPrimary'
                    : 'text-apolo-textTertiary hover:text-apolo-textSecondary'
                }`}
              >
                <t.Icon size={11} aria-hidden="true" />
                <span>{t.label}</span>
              </button>
            );
          })}
        </nav>
        {lastPump && (lastPump.failed > 0 || lastPump.error) && (
          <span
            role="status"
            title={lastPump.error || `${lastPump.failed} vault writes failed`}
            className="text-[10px] text-apolo-warning"
          >
            {lastPump.failed > 0 ? `${lastPump.failed} failed` : 'sync issue'}
          </span>
        )}
        {lastPump && lastPump.succeeded > 0 && lastPump.failed === 0 && !lastPump.error && (
          <span
            role="status"
            className="text-[10px] text-apolo-success"
            title="vaultCommit pump"
          >
            {lastPump.succeeded} applied
          </span>
        )}
      </header>
      <div className="flex-1 overflow-y-auto">
        <ActiveBody />
      </div>
    </section>
  );
}
